import { Pipe, PipeTransform } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

/**
 * Tags Quill's announcement toolbar can emit (bold/italic/underline/strike/link/color/background).
 * Anything else is unwrapped so text survives but scripts/iframes do not.
 */
const ALLOWED_TAGS = new Set([
    'P', 'BR', 'SPAN', 'STRONG', 'EM', 'U', 'S', 'STRIKE', 'B', 'I', 'A', 'DIV'
]);

const ALLOWED_ATTRS = new Set(['href', 'target', 'rel', 'class', 'style', 'title']);

/** Only the style properties Quill's color / background pickers write. */
const ALLOWED_STYLE_PROPS = new Set(['color', 'background-color']);

/**
 * Strip announcement HTML down to Quill-safe markup.
 * Keeps text colour / background colour; drops scripts, event handlers and other tags.
 * Used by the Announcement Management list instead of ngx-quill `[sanitize]="true"`,
 * which runs Angular DomSanitizer and removes every `style` (so colours never show).
 */
export function sanitizeAnnouncementHtml(dirty: string): string {
    if (!dirty || typeof dirty !== 'string') {
        return '';
    }

    const doc = new DOMParser().parseFromString(dirty, 'text/html');
    sanitizeNode(doc.body);
    return doc.body.innerHTML;
}

function sanitizeNode(root: HTMLElement): void {
    const children: Node[] = Array.from(root.childNodes);
    for (const child of children) {
        if (child.nodeType === Node.COMMENT_NODE) {
            root.removeChild(child);
            continue;
        }
        if (child.nodeType !== Node.ELEMENT_NODE) {
            continue;
        }

        const el = child as HTMLElement;
        const tag = el.tagName.toUpperCase();

        if (!ALLOWED_TAGS.has(tag)) {
            // Unwrap: keep descendants, drop the forbidden element itself.
            while (el.firstChild) {
                root.insertBefore(el.firstChild, el);
            }
            root.removeChild(el);
            continue;
        }

        // Drop every attribute that is not on the allow-list, then re-check survivors.
        Array.from(el.attributes).forEach((attr) => {
            const name = attr.name.toLowerCase();
            if (name.startsWith('on') || !ALLOWED_ATTRS.has(name)) {
                el.removeAttribute(attr.name);
            }
        });

        if (el.hasAttribute('style')) {
            const cleaned = sanitizeStyle(el.getAttribute('style') || '');
            if (cleaned) {
                el.setAttribute('style', cleaned);
            } else {
                el.removeAttribute('style');
            }
        }

        if (tag === 'A') {
            const href = (el.getAttribute('href') || '').trim();
            if (!isSafeHref(href)) {
                el.removeAttribute('href');
            } else {
                // Quill / editors sometimes store bare hostnames without a scheme;
                // browsers treat those as relative paths, so normalise to https://.
                el.setAttribute('href', normalizeHref(href));
            }
            const target = el.getAttribute('target');
            if (target && target.toLowerCase() !== '_blank') {
                el.removeAttribute('target');
            }
            if (el.getAttribute('target')?.toLowerCase() === '_blank') {
                el.setAttribute('rel', 'noopener noreferrer');
            }
        }

        sanitizeNode(el);
    }
}

function sanitizeStyle(style: string): string {
    return style
        .split(';')
        .map((part) => part.trim())
        .filter(Boolean)
        .map((decl) => {
            const colon = decl.indexOf(':');
            if (colon < 0) {
                return null;
            }
            const prop = decl.slice(0, colon).trim().toLowerCase();
            const value = decl.slice(colon + 1).trim();
            if (!ALLOWED_STYLE_PROPS.has(prop)) {
                return null;
            }
            // Block CSS-based script vectors; keep rgb()/hex/named colours Quill writes.
            if (/expression|url\s*\(|javascript:|@import/i.test(value)) {
                return null;
            }
            if (!/^(#[0-9a-fA-F]{3,8}|rgba?\(\s*[\d.\s,%]+\s*\)|[a-zA-Z]+)$/.test(value)) {
                return null;
            }
            return `${prop}: ${value}`;
        })
        .filter((entry): entry is string => entry !== null)
        .join('; ');
}

function isSafeHref(href: string): boolean {
    if (!href) {
        return false;
    }
    const lower = href.toLowerCase();
    if (lower.startsWith('javascript:') || lower.startsWith('data:') || lower.startsWith('vbscript:')) {
        return false;
    }
    if (/^(https?:|mailto:|\/|#)/i.test(href)) {
        return true;
    }
    // Link UI can save "example.com" with no scheme.
    return isBareHostname(href);
}

/**
 * Host-looking href with at least one dot (example.com or example.com/path).
 * Rejects spaces and any existing scheme.
 */
function isBareHostname(href: string): boolean {
    if (/\s/.test(href) || href.includes('://') || href.startsWith('.') || href.startsWith('/')) {
        return false;
    }
    return /^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?)+(\/[^\s]*)?$/i.test(
        href
    );
}

function normalizeHref(href: string): string {
    if (/^(https?:|mailto:|\/|#)/i.test(href)) {
        return href;
    }
    if (isBareHostname(href)) {
        return `https://${href}`;
    }
    return href;
}

@Pipe({
    name: 'safeAnnouncementHtml'
})
export class SafeAnnouncementHtmlPipe implements PipeTransform {
    constructor(private readonly sanitizer: DomSanitizer) {}

    transform(value: string | null | undefined): SafeHtml {
        const cleaned = sanitizeAnnouncementHtml(value || '');
        return this.sanitizer.bypassSecurityTrustHtml(cleaned);
    }
}
